import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Table,
  TableProps,
  Box,
  SpaceBetween,
  Button,
  Container,
  Header,
  Alert,
  Link,
  Pagination,
  CollectionPreferencesProps,
} from '@amzn/awsui-components-console';
import { ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router';
import { SearchState, SEARCH_MODE, type SearchMode } from '../SearchPage';
import {
  useSearchSourceStringsLazyQuery,
  useSearchTranslationsLazyQuery,
  SortOrder,
  SourceStringResult,
  TranslationResult,
} from 'src/generated/graphql';
import { TranslationStatusIndicator } from 'src/common/translationStatus';
import {
  SORT_FIELD_ITEM_PROPERTY,
  SORT_FIELD_LABELS,
  sortFieldForItemProperty,
  toggleSortField,
  toServerSort,
  type SortField,
  type SortState,
} from '../sortState';
import {
  MIN_QUERY_LENGTH_MESSAGE,
  MIN_SEARCH_CRITERIA_MESSAGE,
} from '../constants';
import {
  hasMinimumSearchCriteria,
  isQueryTooShort,
} from '../searchFilterUtils';
import {
  loadSearchTablePreferences,
  normaliseSearchTablePreferences,
  saveSearchTablePreferences,
  searchTablePreferencesConfig,
  type SearchTablePreferences,
} from './searchTablePreferences';
import { TablePreferences } from 'src/common/ui/TablePreferences';
import styles from './SearchResultsTable.module.scss';

interface SearchResultsTableProps {
  readonly searchState: SearchState;
  readonly triggerSearch: number;
  readonly onSortChange: (sortState: SortState) => void;
  /**
   * Bumped by `SearchPage` — on Clear, and on a mode switch — to discard the
   * results and return to the pre-search empty state. A counter, not a boolean, so
   * two consecutive resets are two distinguishable edges and both are delivered.
   */
  readonly resetResults: number;
  /**
   * Reports whether results are on screen, so `SearchPage` can enable "Clear
   * filters" while stale rows are showing but every filter control is empty.
   * Reported up because this component owns the result data.
   */
  readonly onHasSearchedChange: (hasSearched: boolean) => void;
}

/**
 * What the last search request put on the wire, so the ordering label describes
 * the rows on screen rather than the live form state — typing updates
 * `searchState.queryText` without issuing a request.
 */
interface RequestOrderingSnapshot {
  /** As transmitted, i.e. already trimmed. Empty means browse mode. */
  readonly queryText: string;
  /** Whether the request carried an explicit `sortBy`. */
  readonly hasExplicitSort: boolean;
}

// --- Hyperlink helpers ---
// TODO: Replace '#' with real route templates once detail pages exist in App.tsx.

/** Build href for a string detail page. */
function buildStringDetailHref(_row: {
  namespaceId: string;
  stringGroupId: string;
  stringId: string;
}): string {
  // TODO: return `/${row.namespaceId}/${row.stringGroupId}/${row.stringId}`;
  return '#';
}

/** Build href for a namespace detail page. */
function buildNamespaceHref(row: { namespaceId: string }): string {
  return `/${encodeURIComponent(row.namespaceId)}`;
}

/** Build href for a string group detail page. */
function buildStringGroupHref(_row: {
  namespaceId: string;
  stringGroupId: string;
}): string {
  // TODO: return `/${row.namespaceId}/${row.stringGroupId}`;
  return '#';
}

/**
 * Internal SPA link for namespace navigation. `onFollow` + `navigate` avoids a
 * full page reload while the `href` keeps middle-click / Cmd+click working.
 * Matches the pattern in `NamespaceSideNav`.
 */
const NamespaceLink: React.FC<{ namespaceId: string }> = ({ namespaceId }) => {
  const navigate = useNavigate();
  const href = buildNamespaceHref({ namespaceId });

  return (
    <span data-testid="row-namespace">
      <Link
        href={href}
        onFollow={(event) => {
          event.preventDefault();
          navigate(href);
        }}
      >
        {namespaceId}
      </Link>
    </span>
  );
};

/**
 * Row action for opening a string's detail page. A `Button` rather than a `Link`
 * because it is an action, not inline text; `ariaLabel` names the row so a screen
 * reader user can tell one row's Details button from another's.
 */
const DetailsButton: React.FC<{
  readonly item: {
    namespaceId: string;
    stringGroupId: string;
    stringId: string;
  };
}> = ({ item }) => {
  const navigate = useNavigate();
  const href = buildStringDetailHref(item);

  return (
    <Button
      variant="normal"
      ariaLabel={`View details for ${item.stringId}`}
      onClick={() => navigate(href)}
    >
      Details
    </Button>
  );
};

/**
 * Extracts user-friendly error info from an Apollo error result.
 * Backend returns InternalServerException (500) for downstream/search failures,
 * ValidationException (400) for input validation errors, and
 * AccessDeniedException (403) when a requested namespace is not viewable.
 */
function classifyError(error: ApolloError): {
  type: 'server' | 'validation' | 'permission';
  message: string;
} {
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    // Checked first: a 403 is not retryable, so it must not fall through to the
    // generic server branch and its Retry button.
    const isAccessDenied = error.graphQLErrors.some(
      (e) =>
        (e.extensions?.['errorType'] as string | undefined) ===
        'AccessDeniedException',
    );
    if (isAccessDenied) {
      return { type: 'permission', message: '' };
    }

    const validationError = error.graphQLErrors.find(
      (e) =>
        (e.extensions?.['code'] as string | undefined) === 'VALIDATION_ERROR' ||
        (e.extensions?.['errorType'] as string | undefined) ===
          'ValidationException',
    );
    if (validationError) {
      return { type: 'validation', message: validationError.message };
    }
  }
  return { type: 'server', message: '' };
}

/**
 * Page size is a user preference, not a constant — see `searchTablePreferences`.
 * Every request builder and the page count reads the live value.
 */

/**
 * Pagination: the backend is cursor-based (`search_after`), so page N is only
 * reachable by fetching pages 1..N-1 to collect their tokens. `pageTokenCache`
 * maps a 1-based page number to the token that fetches it (page 1 needs none),
 * which makes a revisit one request and a first jump ahead N sequential ones —
 * each depends on the previous response, so they cannot be parallelised. The
 * cache is cleared on every new search/sort/filter change, because a cursor is
 * only valid for the result set that produced it.
 *
 * A cursor is equally only valid for the PAGE SIZE that minted it, so a page
 * size change invalidates the cache exactly as a new search does — reset to
 * page 1 and re-issue. Cursors cannot be translated between sizes.
 */

/**
 * Maximum number of pages exposed to the user via the Pagination component.
 * Bounds the worst-case sequential prefetch to MAX_VISIBLE_PAGES - 1 fetches.
 */
const MAX_VISIBLE_PAGES = 20;

/** Truncates long cell text, keeping the full value in a tooltip. */
const TruncatedText: React.FC<{ readonly text: string }> = ({ text }) => (
  <span title={text}>{text.length > 80 ? `${text.slice(0, 80)}…` : text}</span>
);

/**
 * `aria-label` for a sortable header, built from the state Cloudscape reports.
 *
 * It carries the NEXT action, which neither the icon nor `aria-sort` can express:
 * the third activation CLEARS the sort, so without this the backend-decided
 * ordering would be unreachable for a user who cannot see the caret.
 */
const sortHeaderAriaLabel =
  (label: string) =>
  ({ sorted, descending }: TableProps.LabelData): string =>
    !sorted
      ? `${label}, not sorted. Activate to sort.`
      : !descending
        ? `${label}, sorted ascending. Activate to reverse the sort.`
        : `${label}, sorted descending. Activate to clear the sort.`;

/**
 * The header half of a sortable column definition. `sortingField` is the only
 * sorting hint given: `sortingComparator` is omitted so no client-side comparator
 * can be introduced — rows must stay in the order the backend returned them (see
 * `sortState.ts`). Item-agnostic so it can be spread into both column sets.
 */
type SortableHeaderProps = Pick<
  TableProps.ColumnDefinition<unknown>,
  'header' | 'sortingField' | 'ariaLabel'
>;

const sortableHeader = (field: SortField): SortableHeaderProps => ({
  header: SORT_FIELD_LABELS[field],
  sortingField: SORT_FIELD_ITEM_PROPERTY[field],
  ariaLabel: sortHeaderAriaLabel(SORT_FIELD_LABELS[field]),
});

/**
 * Seeded width of the trailing Details column, and its single source of truth:
 * published below as the `--actions-column-width` custom property, which the SCSS
 * pins the column to. See the `th[data-rightmost]` rule there for why the width
 * has to be enforced from CSS.
 */
const ACTIONS_COLUMN_WIDTH = 120;

/**
 * Source-mode columns. Sortable ones declare `sortingField` and let Cloudscape
 * own the header — the click/keyboard target, the caret and `aria-sort`. Widths
 * are numeric because `resizableColumns` needs them to seed the layout.
 *
 * The actions column must stay LAST — the SCSS width pin selects the rightmost
 * column (see `ACTIONS_COLUMN_WIDTH`).
 */
const SOURCE_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<SourceStringResult>[] =
  [
    {
      id: 'stringId',
      ...sortableHeader('STRING_ID'),
      width: 260,
      minWidth: 140,
      cell: (item) => (
        <span data-testid="row-string-id">
          <Link href={buildStringDetailHref(item)}>{item.stringId}</Link>
        </span>
      ),
    },
    {
      id: 'group',
      ...sortableHeader('STRING_GROUP'),
      width: 170,
      minWidth: 120,
      cell: (item) => (
        <Link href={buildStringGroupHref(item)}>{item.stringGroupId}</Link>
      ),
    },
    {
      id: 'namespace',
      ...sortableHeader('NAMESPACE'),
      width: 180,
      minWidth: 120,
      cell: (item) => <NamespaceLink namespaceId={item.namespaceId} />,
    },
    {
      id: 'sourceContent',
      header: 'Source text',
      width: 300,
      minWidth: 160,
      cell: (item) => <TruncatedText text={item.sourceContent} />,
    },
    {
      id: 'status',
      ...sortableHeader('STATUS'),
      width: 150,
      minWidth: 120,
      cell: (item) => (
        <span data-testid="row-status">
          <TranslationStatusIndicator status={item.status} />
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      // Pinned from the SCSS — see ACTIONS_COLUMN_WIDTH.
      width: ACTIONS_COLUMN_WIDTH,
      minWidth: 100,
      cell: (item) => <DetailsButton item={item} />,
    },
  ];

/**
 * Translation-mode columns. No `STATUS` — `TranslationResult` has no `status`
 * member — and `locale` is rendered here only, since `SourceStringResult` has no
 * equivalent.
 */
const TRANSLATION_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<TranslationResult>[] =
  [
    {
      id: 'stringId',
      ...sortableHeader('STRING_ID'),
      width: 260,
      minWidth: 140,
      cell: (item) => (
        <span data-testid="row-string-id">
          <Link href={buildStringDetailHref(item)}>{item.stringId}</Link>
        </span>
      ),
    },
    {
      id: 'group',
      ...sortableHeader('STRING_GROUP'),
      width: 170,
      minWidth: 120,
      cell: (item) => (
        <Link href={buildStringGroupHref(item)}>{item.stringGroupId}</Link>
      ),
    },
    {
      id: 'namespace',
      ...sortableHeader('NAMESPACE'),
      width: 180,
      minWidth: 120,
      cell: (item) => <NamespaceLink namespaceId={item.namespaceId} />,
    },
    {
      id: 'localizedText',
      header: 'Localized text',
      width: 320,
      minWidth: 160,
      cell: (item) => <TruncatedText text={item.localizedText} />,
    },
    /*
     * Locale is deliberately PLAIN: no `sortingField`, so no sort affordance and
     * no `aria-sort`. The backend `SortBy` enum has no LOCALE value and all
     * ordering is server-side, so a clickable header here could not sort
     * anything. Make it sortable only once `SortBy` gains LOCALE (adding it to
     * `SortField`, `SERVER_SORT_BY` and `SORT_FIELD_ITEM_PROPERTY` in
     * `sortState.ts` at the same time).
     */
    {
      id: 'locale',
      header: 'Locale',
      width: 140,
      minWidth: 100,
      cell: (item) => <span data-testid="row-locale">{item.locale}</span>,
    },
    {
      id: 'actions',
      header: '',
      // Pinned from the SCSS — see ACTIONS_COLUMN_WIDTH.
      width: ACTIONS_COLUMN_WIDTH,
      minWidth: 100,
      cell: (item) => <DetailsButton item={item} />,
    },
  ];

const SearchResultsTable: React.FC<SearchResultsTableProps> = ({
  searchState,
  triggerSearch,
  onSortChange,
  resetResults,
  onHasSearchedChange,
}) => {
  const isSource = searchState.mode === SEARCH_MODE.SOURCE;
  const { sortState } = searchState;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [validationWarning, setValidationWarning] = useState<string | null>(
    null,
  );
  // Whether we're in a multi-page prefetch sequence (jumping to an unvisited page)
  const [isPrefetching, setIsPrefetching] = useState(false);

  /*
   * Preferences held PER MODE: the component does not remount on a mode switch,
   * so a single `preferences` state would carry one mode's column ids into the
   * other table. The lazy initialiser reads storage once, on mount.
   */
  const [preferencesByMode, setPreferencesByMode] = useState<
    Record<SearchMode, SearchTablePreferences>
  >(() => ({
    [SEARCH_MODE.SOURCE]: loadSearchTablePreferences(SEARCH_MODE.SOURCE),
    [SEARCH_MODE.TRANSLATION]: loadSearchTablePreferences(
      SEARCH_MODE.TRANSLATION,
    ),
  }));
  const preferences = preferencesByMode[searchState.mode];
  const { pageSize } = preferences;

  /*
   * The ordering-relevant parts of the last issued request — what backs the
   * ordering label. State rather than a ref because the label must re-render;
   * safe because no effect depends on it, so setting it cannot retrigger a
   * search.
   */
  const [orderingSnapshot, setOrderingSnapshot] =
    useState<RequestOrderingSnapshot | null>(null);

  // Page number → the token that fetches it (page 1 needs none). A ref so cache
  // writes do not re-render; the state changes from fetches do that.
  const pageTokenCache = useRef<Map<number, string | undefined>>(
    new Map([[1, undefined]]),
  );
  // Furthest page whose response we have seen, i.e. the highest page whose
  // successor token is known.
  const furthestPageFetched = useRef<number>(0);

  // Cancels in-flight sequential prefetches: every increment invalidates all
  // prior async loops.
  const searchGeneration = useRef<number>(0);

  const previousTrigger = useRef(0);
  const previousReset = useRef(0);

  /**
   * Whether results are on screen: a search has run and has not been discarded
   * since. Drives the empty state, the rows, the `(count)`, the pagination, the
   * alerts and the spinner.
   *
   * It is also the RESET MECHANISM, not just a display flag. Apollo's lazy-query
   * `data` outlives a reset and there is no supported way to clear it, so the
   * reset GATES that data instead — which is what also makes it safe against a
   * response still in flight when Clear is pressed.
   */
  const [hasSearched, setHasSearched] = useState(false);
  /**
   * Synchronous mirror of `hasSearched`, for readers that need the value as it is
   * when a RESPONSE LANDS. `setHasSearched(false)` is not visible until React
   * re-renders, so a callback firing in between would read a stale `true` and let
   * a superseded response back in. Every writer sets both on adjacent lines.
   */
  const hasSearchedRef = useRef(false);

  const [executeSourceSearch, sourceResult] = useSearchSourceStringsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [executeTranslationSearch, translationResult] =
    useSearchTranslationsLazyQuery({
      fetchPolicy: 'network-only',
    });

  const result = isSource ? sourceResult : translationResult;
  /*
   * Gated on `hasSearched`: a request still in flight when Clear is pressed would
   * otherwise put "Searching..." over the pre-search empty state.
   */
  const loading = hasSearched && (result.loading || isPrefetching);
  const error = result.error;

  /*
   * The single criteria gate for the component: the warning to surface, or null
   * when `searchState` may be sent to the backend. `SearchPage`
   * already gates every user-initiated submit, so this covers the paths that
   * re-fire an existing search after the query has been emptied — the sort
   * effect, Retry and pagination.
   *
   * Standalone rather than part of `doSearch` because there are TWO
   * request-issuing paths: `doSearch`, and the prefetch loop in
   * `handlePageChange`, which builds its own variables and calls the executors
   * directly. Both must consult this.
   *
   * Not the last line of defence: the backend rejects a search constrained by
   * neither query text nor any filter with a 400 ValidationException, and counts a
   * blank `queryText` as omitted. This gate exists so those doomed requests never
   * leave the browser and the reason is shown inline. An empty `queryText` is still
   * a valid request once a filter is set. See `hasMinimumSearchCriteria`.
   */
  const criteriaWarning = useCallback((): string | null => {
    const { queryText, filters } = searchState;

    /*
     * Checked before the mode's own required filters: a term under
     * `MIN_QUERY_LENGTH` blocks the request whatever else is selected.
     */
    if (isQueryTooShort(queryText)) {
      return MIN_QUERY_LENGTH_MESSAGE;
    }
    // TRANSLATION mode — translatedLocales is required
    if (
      !isSource &&
      (!filters.translatedLocales || filters.translatedLocales.length === 0)
    ) {
      return 'At least one translated locale is required for translation search.';
    }
    // SOURCE mode — at least one criterion is required
    if (isSource && !hasMinimumSearchCriteria(filters, queryText)) {
      return MIN_SEARCH_CRITERIA_MESSAGE;
    }
    return null;
  }, [searchState, isSource]);

  const doSearch = useCallback(
    /**
     * `overridePageSize` is for one caller — the preferences confirm handler,
     * which re-searches in the same tick as its `setState`, so this callback
     * still closes over the old size. Every other caller omits it.
     */
    (token?: string, overridePageSize?: number) => {
      const { queryText, exactMatch, filters } = searchState;

      const warning = criteriaWarning();
      if (warning) {
        setValidationWarning(warning);
        return;
      }
      setValidationWarning(null);

      /*
       * SPREAD, not two named fields: an unsorted state must omit both KEYS so
       * the backend takes its own resolution path rather than receiving
       * `sortBy: undefined`.
       */
      const serverSort = toServerSort(sortState);

      // Trimmed so the request agrees with `hasMinimumSearchCriteria`, which also
      // trims: a plain `queryText || undefined` would let "   " through.
      const trimmedQueryText = queryText.trim();

      /*
       * Recorded AFTER the criteria gate, so a skipped request leaves the label
       * describing the rows still on screen.
       */
      setOrderingSnapshot({
        queryText: trimmedQueryText,
        hasExplicitSort: serverSort.sortBy !== undefined,
      });

      const baseInput = {
        queryText: trimmedQueryText || undefined,
        exactMatch,
        filters: {
          namespaceIds: filters.namespaceIds,
          stringGroupIds: filters.stringGroupIds,
          cxIds: filters.cxIds,
          cxFamilies: filters.cxFamilies,
          translatedLocales: filters.translatedLocales,
        },
        ...serverSort,
        pageSize: overridePageSize ?? pageSize,
        nextToken: token,
      };

      if (isSource) {
        executeSourceSearch({
          variables: {
            input: {
              ...baseInput,
              filters: {
                ...baseInput.filters,
                statuses: filters.statuses,
              },
            },
          },
        });
      } else {
        executeTranslationSearch({
          variables: { input: baseInput },
        });
      }
    },
    [
      searchState,
      sortState,
      isSource,
      pageSize,
      criteriaWarning,
      executeSourceSearch,
      executeTranslationSearch,
    ],
  );

  const doSearchRef = useRef(doSearch);
  doSearchRef.current = doSearch;

  /** Reset pagination state — called on new search, sort, or filter change */
  const resetPagination = useCallback(() => {
    searchGeneration.current++;
    setCurrentPage(1);
    pageTokenCache.current = new Map([[1, undefined]]);
    furthestPageFetched.current = 0;
    setIsPrefetching(false);
  }, []);

  // Reset pagination state when search mode changes to prevent stale
  // page-token cache and totalCount from the other mode leaking through.
  const previousMode = useRef(searchState.mode);
  useEffect(() => {
    if (searchState.mode !== previousMode.current) {
      previousMode.current = searchState.mode;
      resetPagination();
      setTotalCount(0);
      setValidationWarning(null);
      /*
       * Mode swaps which query's data is rendered WITHOUT issuing a request, so
       * the remembered request no longer describes the rows on screen. Clear it
       * until the next request records a new one.
       */
      setOrderingSnapshot(null);
    }
  }, [searchState.mode, resetPagination]);

  // React to search trigger (new search from button click)
  useEffect(() => {
    if (triggerSearch > 0 && triggerSearch !== previousTrigger.current) {
      previousTrigger.current = triggerSearch;
      hasSearchedRef.current = true;
      setHasSearched(true);
      resetPagination();
      doSearchRef.current(undefined);
    }
  }, [triggerSearch, resetPagination]);

  /*
   * Clear: discard the results and go back to the pre-search empty state,
   * ISSUING NO REQUEST (see `handleClearFilters` in `SearchPage`).
   *
   * Everything the displayed results consist of is reset, because a surviving
   * page token is a cursor into a result set the user can no longer see.
   *
   * Two in-flight requests need cancelling, by different mechanisms: the
   * sequential prefetch loop bails on the generation bump inside
   * `resetPagination`; a lone `doSearch` has no loop to bail out of, so its
   * response is instead GATED by `hasSearchedRef` when it lands.
   *
   * Runs after the trigger effect on purpose: if a bump and a Clear landed in the
   * same commit, the Clear wins as the more recent user intent.
   */
  useEffect(() => {
    if (resetResults > 0 && resetResults !== previousReset.current) {
      previousReset.current = resetResults;
      hasSearchedRef.current = false;
      setHasSearched(false);
      resetPagination();
      setTotalCount(0);
      setValidationWarning(null);
      setOrderingSnapshot(null);
    }
  }, [resetResults, resetPagination]);

  // Keep `SearchPage` in step, so "Clear" can be enabled whenever there is
  // something to discard — including when every filter control is empty.
  useEffect(() => {
    onHasSearchedChange(hasSearched);
  }, [hasSearched, onHasSearchedChange]);

  /*
   * Re-query on every sort change: ordering is entirely server-side, and the
   * cached `search_after` tokens are cursors into the order that produced them,
   * so pagination has to reset with it. The optional chaining covers entering and
   * leaving the unsorted state, which are sort changes like any other.
   *
   * Gated on `hasSearchedRef` so a sort change after a Clear cannot resurrect the
   * discarded search.
   */
  useEffect(() => {
    if (hasSearchedRef.current) {
      resetPagination();
      doSearchRef.current(undefined);
    }
  }, [sortState?.field, sortState?.order, resetPagination]);

  // Update totalCount and page-token cache from results
  useEffect(() => {
    // During sequential prefetch the loop manages the cache itself; skip so a
    // stale `currentPage` cannot corrupt entries.
    if (isPrefetching) return;

    /*
     * ONLY EVER CACHE FROM SETTLED DATA. While a request is in flight `data` still
     * holds the PREVIOUS result set, so a re-run in that window would file that
     * set's page cursor under a page number of the incoming one — a cursor into
     * results the user can no longer see. Waiting costs nothing: `loading` going
     * false is itself a dependency change.
     */
    if (result.loading) return;

    /*
     * THE RESET RACE. A response for a request that was in flight when Clear was
     * pressed still lands here and must change NOTHING — `totalCount` alone would
     * bring back the `(count)` and the pagination over an empty table.
     *
     * Read off the REF, not the state: this closure belongs to the render that
     * was current when the response arrived, so after a `setHasSearched(false)`
     * that has not yet re-rendered the state variable still reads `true`.
     */
    if (!hasSearchedRef.current) return;

    const conn = isSource
      ? sourceResult.data?.searchSourceStrings
      : translationResult.data?.searchTranslations;

    if (conn) {
      setTotalCount(conn.totalCount);
      // Cache the nextToken for the NEXT page (currentPage + 1)
      if (conn.nextToken) {
        pageTokenCache.current.set(currentPage + 1, conn.nextToken);
      }
      // Track furthest page we've fetched
      if (currentPage > furthestPageFetched.current) {
        furthestPageFetched.current = currentPage;
      }
    }
  }, [
    isPrefetching,
    isSource,
    sourceResult.data,
    translationResult.data,
    currentPage,
    /*
     * The settle edge, and load-bearing rather than belt-and-braces: the gate
     * above makes every in-flight run a no-op, and `data` cannot substitute —
     * a repeated query normalises to the same cached object, so its identity does
     * not change. Removing this breaks the post-reset re-search (test:
     * "repopulates the counter and pagination ... even when Apollo returns
     * identity-stable data").
     */
    result.loading,
    /*
     * The second edge, for a response that has already settled by the time
     * `hasSearched` flips, where there is no `loading` transition left to observe.
     * It cannot re-admit a post-reset response — the `hasSearchedRef` gate rejects
     * that direction.
     */
    hasSearched,
  ]);

  /**
   * Page change from Cloudscape `Pagination`. Fetches directly when the target
   * page's token is cached, otherwise walks forward collecting tokens.
   */
  const handlePageChange = useCallback(
    async (targetPage: number) => {
      // Same page — no-op
      if (targetPage === currentPage) return;

      /*
       * Criteria gate — MUST run here, before either request path below. Editing
       * the query does not reset pagination, so Pagination stays enabled after the
       * user empties it; the prefetch loop bypasses `doSearch`, so without this it
       * could fire up to MAX_VISIBLE_PAGES - 1 unbounded browse queries at once.
       */
      const warning = criteriaWarning();
      if (warning) {
        setValidationWarning(warning);
        return;
      }
      setValidationWarning(null);

      const cachedToken = pageTokenCache.current.get(targetPage);
      if (cachedToken !== undefined || targetPage === 1) {
        // Token is cached (or it's page 1 which needs no token) — direct fetch
        setCurrentPage(targetPage);
        doSearchRef.current(targetPage === 1 ? undefined : cachedToken);
        return;
      }

      // Target page is beyond our cached range — need to sequentially fetch
      // intermediate pages to collect their nextTokens.
      const gen = ++searchGeneration.current;
      setIsPrefetching(true);

      // Highest page we can start from: a page's successor token is only known if
      // that page's response was actually seen.
      let startPage = 1;
      for (let p = targetPage - 1; p >= 1; p--) {
        if (pageTokenCache.current.has(p)) {
          if (p <= furthestPageFetched.current) {
            startPage = p + 1;
            break;
          }
        }
      }

      /* istanbul ignore next -- unreachable: the cache always holds page 1, and startPage is only set to p+1 for a fetched page whose successor token is cached */
      if (!pageTokenCache.current.has(startPage)) {
        startPage = furthestPageFetched.current + 1;
      }

      // Sequential fetch loop: fetch pages startPage..targetPage
      const { queryText, exactMatch, filters } = searchState;
      // Spread and trimmed exactly as in `doSearch`, so the two request builders
      // transmit the same thing.
      const serverSort = toServerSort(sortState);
      const trimmedQueryText = queryText.trim();

      /*
       * The SECOND request-issuing path, so it records the snapshot too — this
       * loop calls the executors directly. Written synchronously with the `gen`
       * bump above and never again, so a superseded loop cannot overwrite a newer
       * value: it returns at its first generation check instead.
       */
      setOrderingSnapshot({
        queryText: trimmedQueryText,
        hasExplicitSort: serverSort.sortBy !== undefined,
      });

      for (let page = startPage; page <= targetPage; page++) {
        const token = pageTokenCache.current.get(page);
        /* istanbul ignore next -- unreachable: only page 1 has an undefined token; every other page's token comes from the previous page's response */
        if (token === undefined && page !== 1) {
          setIsPrefetching(false);
          return;
        }

        const baseInput = {
          queryText: trimmedQueryText || undefined,
          exactMatch,
          filters: {
            namespaceIds: filters.namespaceIds,
            stringGroupIds: filters.stringGroupIds,
            cxIds: filters.cxIds,
            cxFamilies: filters.cxFamilies,
            translatedLocales: filters.translatedLocales,
          },
          ...serverSort,
          pageSize,
          nextToken: token,
        };

        const variables = isSource
          ? {
              input: {
                ...baseInput,
                filters: { ...baseInput.filters, statuses: filters.statuses },
              },
            }
          : { input: baseInput };

        try {
          let data:
            | { nextToken?: string | null; totalCount: number }
            | null
            | undefined;

          if (isSource) {
            const response = await executeSourceSearch({ variables });
            // `execute()` resolves even on GraphQL errors — it never rejects — so
            // throw explicitly to reach the catch block below.
            if (response.error) throw response.error;
            // Bail out if a reset or newer invocation superseded this one
            if (gen !== searchGeneration.current) {
              setIsPrefetching(false);
              return;
            }
            data = response.data?.searchSourceStrings;
          } else {
            const response = await executeTranslationSearch({ variables });
            if (response.error) throw response.error;
            // Bail out if a reset or newer invocation superseded this one
            if (gen !== searchGeneration.current) {
              setIsPrefetching(false);
              return;
            }
            data = response.data?.searchTranslations;
          }

          if (data?.nextToken) {
            pageTokenCache.current.set(page + 1, data.nextToken);
          }
          if (page > furthestPageFetched.current) {
            furthestPageFetched.current = page;
          }
          if (data) {
            setTotalCount(data.totalCount);
          }
        } catch {
          // Stop and stay where we landed, but only if this invocation is still
          // current: an unguarded `setCurrentPage` after a Clear would leave
          // `currentPage > 1` in the pre-search state.
          setIsPrefetching(false);
          if (gen === searchGeneration.current) {
            setCurrentPage(page);
          }
          return;
        }
      }

      setIsPrefetching(false);
      // Only update currentPage if this generation is still active
      if (gen === searchGeneration.current) {
        setCurrentPage(targetPage);
      }
    },
    [
      currentPage,
      searchState,
      sortState,
      isSource,
      pageSize,
      criteriaWarning,
      executeSourceSearch,
      executeTranslationSearch,
    ],
  );

  /**
   * Header activation → next sort state (which refetches from page 1).
   *
   * The event's own `isDescending` is ignored on purpose: Cloudscape's native
   * cycle is two-state, so it cannot express the third activation.
   * `toggleSortField` stays the authority, preserving ascending → descending →
   * unsorted, which is the only route back to the backend-decided ordering. See
   * `sortState.ts`.
   */
  const handleSortingChange = useCallback(
    (sortingField: string | undefined) => {
      const field = sortFieldForItemProperty(sortingField);
      if (field) {
        onSortChange(toggleSortField(sortState, field));
      }
    },
    [onSortChange, sortState],
  );

  /**
   * Preferences confirm → normalise, apply, persist, and re-search if the page
   * size moved: cached `search_after` tokens are cursors into one page size, so
   * a size change must clear the cache and restart at page 1. A column-only
   * change issues no request — visibility and order render client-side.
   */
  const handlePreferencesConfirm = useCallback(
    (confirmed: CollectionPreferencesProps.Preferences) => {
      const mode = searchState.mode;
      const next = normaliseSearchTablePreferences(confirmed, mode);
      const pageSizeChanged =
        next.pageSize !== preferencesByMode[mode].pageSize;

      setPreferencesByMode((current) => ({ ...current, [mode]: next }));
      saveSearchTablePreferences(mode, next);

      if (pageSizeChanged) {
        resetPagination();
        // Explicit size: the state write above is not visible to `doSearch` yet.
        doSearchRef.current(undefined, next.pageSize);
      }
    },
    [searchState.mode, preferencesByMode, resetPagination],
  );

  /**
   * The sort state in the shape Cloudscape marks headers with; `undefined` while
   * unsorted, which leaves every sortable header neutral. Matched to the column
   * definitions by `sortingField` rather than object identity.
   */
  const activeSortingColumn = useMemo(
    () =>
      sortState
        ? { sortingField: SORT_FIELD_ITEM_PROPERTY[sortState.field] }
        : undefined,
    [sortState],
  );
  const sortingDescending = sortState?.order === SortOrder.Desc;

  /*
   * Rows render in exactly the order the backend returned them. No client-side
   * re-sort: it could disagree with the collation the backend paginated by and
   * make the sequence non-monotonic across pages. See `sortState.ts`.
   */
  const sourceData = sourceResult.data?.searchSourceStrings?.items;
  const translationData = translationResult.data?.searchTranslations?.items;

  /*
   * The `hasSearched` term is the RESET GATE, not decoration: Apollo keeps the
   * last lazy-query result, so after a reset `sourceData` still holds the
   * discarded rows. The early return below means these are no longer read in the
   * reset state, so the gate is now defence-in-depth — kept so the memos stay
   * correct in isolation. `useMemo` also keeps the empty-array identity stable.
   */
  const sourceItems = useMemo(
    () => (hasSearched ? (sourceData ?? []) : []),
    [hasSearched, sourceData],
  );
  const translationItems = useMemo(
    () => (hasSearched ? (translationData ?? []) : []),
    [hasSearched, translationData],
  );

  /* Live page size, not a constant — a fixed one would offer empty pages. */
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  // Cap visible pages to bound worst-case sequential prefetch to MAX_VISIBLE_PAGES - 1 fetches
  const visiblePages = Math.min(totalPages, MAX_VISIBLE_PAGES);

  /*
   * Rendered before the first search, and again after Clear or a mode switch:
   * nothing table-shaped at all, because an empty `Table`'s "no results" framing
   * describes a search that never ran. A `Container` rather than `null` so the
   * results panel does not appear from nowhere on the first search.
   *
   * Must stay below every hook — the effects above still run in this state: the
   * trigger effect is what leaves it, the reset effect is what got here.
   */
  if (!hasSearched) {
    return (
      <Container>
        <Box
          textAlign="center"
          color="inherit"
          padding={{ top: 'xxl', bottom: 'xxl' }}
          data-testid="pre-search-empty-state"
        >
          <SpaceBetween size="xxs">
            <Box variant="h3" padding="n">
              Search for strings
            </Box>
            <Box variant="p" color="text-body-secondary">
              Enter a search term or select at least one filter, then choose
              Search to see results.
            </Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  /* The table's only empty state: a search ran and matched nothing. */
  const emptyContent = (
    <Box
      textAlign="center"
      color="inherit"
      padding={{ top: 'l', bottom: 'l' }}
      data-testid="no-results-empty-state"
    >
      <SpaceBetween size="xxs">
        <b>No results found</b>
        <Box variant="p" color="text-body-secondary">
          Try adjusting your search or filters.
        </Box>
      </SpaceBetween>
    </Box>
  );

  /*
   * `hasSearched` is true by construction below here — the early return above is
   * the gate — so these branches do not re-test it. The remaining reads are all
   * above the return, where the flag is still doing real work.
   */

  // Criteria gate warning — unmet source-mode criteria, or translation mode
  // with no locale selected (see `criteriaWarning`)
  if (validationWarning) {
    return (
      <Alert type="warning" statusIconAriaLabel="Warning">
        {validationWarning}
      </Alert>
    );
  }

  // Error handling
  if (error) {
    const classified = classifyError(error);

    if (classified.type === 'validation') {
      return (
        <Alert
          type="warning"
          statusIconAriaLabel="Warning"
          header="Validation error"
        >
          {classified.message}
        </Alert>
      );
    }

    /*
     * Deliberately names no namespace, even though the selection is known here:
     * the backend withholds it so search cannot be used to probe whether a
     * namespace exists, and naming it client-side would reopen that. No Retry
     * either — the same request will be refused again.
     */
    if (classified.type === 'permission') {
      return (
        <Alert type="error" statusIconAriaLabel="Error" header="Access denied">
          You don&apos;t have access to one or more of the selected namespaces.
          Change the Namespace filter, or request access to those namespaces,
          then search again.
        </Alert>
      );
    }

    return (
      <Alert type="error" statusIconAriaLabel="Error" header="Search failed">
        An error occurred while searching. This may be caused by an upstream
        service dependency issue. Please try again in a few minutes.
        <Box margin={{ top: 's' }}>
          <Button
            onClick={() => doSearch(pageTokenCache.current.get(currentPage))}
          >
            Retry
          </Button>
        </Box>
      </Alert>
    );
  }

  const paginationComponent = totalCount > 0 && (
    <Pagination
      currentPageIndex={currentPage}
      pagesCount={visiblePages}
      disabled={loading}
      onChange={({ detail }) => handlePageChange(detail.currentPageIndex)}
      ariaLabels={{
        /*
         * Names the pagination GROUP. The Search page renders a second pagination — the
         * "My Namespaces" side nav's `PaginationControl` — with identical per-button
         * labels, so without a group label a screen reader user cannot tell which
         * pagination they are in. This is the case Cloudscape documents the property for.
         */
        paginationLabel: 'Search results pagination',
        nextPageLabel: 'Next page',
        previousPageLabel: 'Previous page',
        pageLabel: (pageNumber) => `Page ${pageNumber}`,
      }}
    />
  );

  /*
   * Rendered through the Table's `preferences` slot, beside the pagination.
   * Unlike the pagination it is NOT gated on `totalCount` — the preferences still
   * apply over a zero-result search. The offered set and its wiring live in the
   * shared component; density is one of them, so it is no longer fixed here.
   */
  const preferencesComponent = (
    <TablePreferences
      config={searchTablePreferencesConfig(searchState.mode)}
      preferences={preferences}
      onConfirm={handlePreferencesConfirm}
    />
  );

  /*
   * Names the ordering the user is looking at when — and only when — the backend
   * ordered by RELEVANCE, inferred from what the last request transmitted. Read off
   * `orderingSnapshot`, not the live `searchState`, which changes as the user types
   * without refetching:
   *
   *   no sortBy sent + query text sent → 'Sorted by relevance'
   *   no sortBy sent + no query text   → nothing; the backend's recency default is
   *                                      deliberately left unlabelled.
   *   explicit sortBy sent             → nothing; that column's own indicator
   *                                      already says it.
   *
   * Relevance is the case worth naming: no column header is marked, and the
   * ordering corresponds to no column the user could have clicked.
   *
   * CAVEAT — this is inference, not a report. The response does not echo which
   * sort branch the backend applied, so the label mirrors
   * `OpenSearchQueryHelper.buildPrimarySort` from memory (see `sortState.ts`). If
   * that resolution order changes, this label must change with it.
   */
  const effectiveOrdering =
    orderingSnapshot &&
    !orderingSnapshot.hasExplicitSort &&
    orderingSnapshot.queryText
      ? 'Sorted by relevance'
      : undefined;

  const resultsHeader = (
    <Header
      counter={`(${totalCount})`}
      description={
        effectiveOrdering && (
          <span data-testid="effective-ordering">{effectiveOrdering}</span>
        )
      }
    >
      Results
    </Header>
  );

  /*
   * `variant="container"` makes the table its own panel, separate from the Filters
   * panel above. The wrapper div scopes the link styling and the trailing-column
   * width pin — see the SCSS.
   */
  return (
    <div
      className={styles.tableWrapper}
      /*
       * Publishes ACTIONS_COLUMN_WIDTH to the SCSS width pin, so the value is not
       * duplicated as a literal there. The cast is needed because
       * `React.CSSProperties` does not model custom properties.
       */
      style={
        {
          '--actions-column-width': `${ACTIONS_COLUMN_WIDTH}px`,
        } as React.CSSProperties
      }
    >
      {isSource ? (
        <Table<SourceStringResult>
          columnDefinitions={SOURCE_COLUMN_DEFINITIONS}
          columnDisplay={preferences.contentDisplay}
          items={sourceItems}
          loading={loading}
          loadingText="Searching..."
          empty={emptyContent}
          variant="container"
          wrapLines={preferences.wrapLines}
          stripedRows={preferences.stripedRows}
          contentDensity={preferences.contentDensity}
          resizableColumns={true}
          sortingColumn={activeSortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) =>
            handleSortingChange(detail.sortingColumn.sortingField)
          }
          header={resultsHeader}
          pagination={paginationComponent}
          preferences={preferencesComponent}
        />
      ) : (
        <Table<TranslationResult>
          columnDefinitions={TRANSLATION_COLUMN_DEFINITIONS}
          columnDisplay={preferences.contentDisplay}
          items={translationItems}
          loading={loading}
          loadingText="Searching..."
          empty={emptyContent}
          variant="container"
          wrapLines={preferences.wrapLines}
          stripedRows={preferences.stripedRows}
          contentDensity={preferences.contentDensity}
          resizableColumns={true}
          sortingColumn={activeSortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) =>
            handleSortingChange(detail.sortingColumn.sortingField)
          }
          header={resultsHeader}
          pagination={paginationComponent}
          preferences={preferencesComponent}
        />
      )}
    </div>
  );
};

export default SearchResultsTable;
